import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Inicializar ambos desde localStorage para evitar desincronización
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    try {
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem("token");
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      // Validar que token y user sean consistentes
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      // Si uno existe pero no el otro, limpiar ambos
      if ((storedToken && !storedUser) || (!storedToken && storedUser)) {
        console.warn("⚠️ Token y User desincronizados, limpiando...");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setToken(null);
        setUser(null);
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(userData));

      setToken(newToken);
      setUser(userData);

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Error al iniciar sesión",
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);

      // Registration successful but user needs approval
      return {
        success: true,
        data: response.data,
        message:
          response.data.message ||
          "Registro exitoso. Tu cuenta está pendiente de aprobación del administrador.",
      };
    } catch (error) {
      console.error("Error al registrarse:", error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Error al registrarse",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isAdmin = () => {
    return hasRole("admin");
  };

  const isInvestigador = () => {
    return hasRole("researcher");
  };

  const isEstudiante = () => {
    return hasRole("collaborator");
  };

  const isStudent = () => {
    return hasRole("student");
  };

  const isLegal = () => {
    return hasRole("legal");
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    isAdmin,
    isInvestigador,
    isEstudiante,
    isStudent,
    isLegal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
